import React, { useMemo, useRef, useState } from 'react';
import { Box } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { WhatsappConversation, WhatsappNumber } from '../../../utils/types';
import {
  formatInboxTime,
  isWindowExpired,
  WhatsAppConversationRow,
  WhatsAppSearchBar,
  WhatsAppSidebarEmpty,
  WhatsAppSidebarHeader,
  WhatsAppSidebarSkeleton,
} from '../components/whatsappInboxUi';

interface ChatSidebarProps {
  orgId: string;
  whatsappNumber?: WhatsappNumber | null;
  conversations: WhatsappConversation[];
  selectedConversationId?: string;
  isLoading?: boolean;
  isError?: boolean;
  errorMessage?: string;
  onRefresh?: () => void;
  onOpenSettings?: () => void;
}

const ChatSidebar: React.FC<ChatSidebarProps> = ({
  orgId,
  whatsappNumber,
  conversations,
  selectedConversationId,
  isLoading = false,
  isError = false,
  errorMessage,
  onRefresh,
  onOpenSettings,
}) => {
  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement | null>(null);
  const navigate = useNavigate();

  const filteredConversations = useMemo(() => {
    const query = search.trim().toLowerCase();
    return conversations.filter((conversation) => {
      const name = `${conversation.lead?.first_name || ''} ${conversation.lead?.second_name || ''}`.trim();
      const phone = conversation.lead?.phone || conversation.recipient_phone || '';
      const preview = conversation.last_message?.text || '';
      return !query || [name, phone, preview].join(' ').toLowerCase().includes(query);
    });
  }, [conversations, search]);

  return (
    <>
      <WhatsAppSidebarHeader
        title="WhatsApp Inbox"
        subtitle={whatsappNumber?.display_name || whatsappNumber?.phone_number || 'Business account'}
        onRefresh={onRefresh}
        onSettings={onOpenSettings}
      />

      <WhatsAppSearchBar value={search} onChange={setSearch} inputRef={searchRef} />

      <Box flex={1} overflowY="auto" bg="white">
        {isLoading ? <WhatsAppSidebarSkeleton /> : null}

        {!isLoading && isError ? (
          <WhatsAppSidebarEmpty
            title="Inbox unavailable"
            description={errorMessage || 'Conversations could not be loaded right now.'}
          />
        ) : null}

        {!isLoading && !isError && filteredConversations.length === 0 ? (
          <WhatsAppSidebarEmpty
            title={conversations.length === 0 ? 'No conversations yet' : 'No matching conversations'}
            description={
              conversations.length === 0
                ? 'When customers message this WhatsApp number, conversations will appear here.'
                : 'Adjust the search term to find a different conversation.'
            }
          />
        ) : null}

        {!isLoading && !isError && filteredConversations.length > 0 ? (
          <Box py={1}>
            {filteredConversations.map((conversation) => {
              const leadName =
                conversation.lead?.first_name
                  ? `${conversation.lead.first_name} ${conversation.lead.second_name || ''}`.trim()
                  : conversation.recipient_phone || 'Unknown contact';

              return (
                <WhatsAppConversationRow
                  key={conversation.id}
                  title={leadName}
                  subtitle={conversation.last_message?.text || 'No messages yet'}
                  time={formatInboxTime(conversation.last_message?.timestamp || conversation.last_message_at)}
                  avatarName={leadName}
                  active={String(selectedConversationId) === String(conversation.id)}
                  expired={isWindowExpired(conversation.open_window_expires_at)}
                  onClick={() =>
                    navigate(`/organization/${orgId}/inbox/whatsapp/conversations/${conversation.id}`)
                  }
                />
              );
            })}
          </Box>
        ) : null}
      </Box>
    </>
  );
};

export default ChatSidebar;
